import React from "react";
import { BrowserRouter as Router, useNavigate, NavLink } from "react-router";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { ProfileDropdown } from "./components/ProfileDropdown";
import { MobileNav } from "./components/MobileNav";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "./components/ui/toaster";
import { TeamSelector } from "./components/TeamSelector";
import { AppRoutes } from "./routes";

// Import the AuthProvider and useAuth hook from the new location
import { AuthProvider, useAuth } from "@/providers/AuthProvider";
import { supabase } from "@/index";

// Create a new QueryClient instance
const queryClient = new QueryClient();

function AppContent() {
  const { user, teams } = useAuth();
  const navigate = useNavigate();

  // Check if user has teams
  const hasTeams = teams && teams.length > 0;

  async function handleSignOut() {
    await supabase.auth.signOut();
    navigate("/");
  }

  async function handleSignIn() {
    navigate("/");
  }

  function renderNavLink(name: string, to: string, onClick?: () => void) {
    if (onClick) {
      return <Button onClick={onClick}>{name}</Button>;
    }
    return (
      <NavLink
        to={to}
        className={({ isActive }) =>
          cn(
            "font-semibold relative py-5",
            isActive &&
              "after:absolute after:left-0 after:bottom-0 after:w-full after:h-0.5 after:bg-primary"
          )
        }
      >
        {name}
      </NavLink>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <nav className="border-b border-b-gray-100 bg-background">
        <div className="container flex h-16 items-center justify-between px-4 max-w-5xl mx-auto">
          <div className="flex-shrink-0">
            <NavLink to="/">
              <img alt="HireUp Logo" src="/logo.png" width={160} />
            </NavLink>
          </div>
          <div className="hidden md:flex w-full justify-between ml-8">
            {user ? (
              <>
                <div className="flex justify-between items-center w-full">
                  <div className="flex items-center space-x-6">
                    {hasTeams && (
                      <>
                        {renderNavLink("Dashboard", "/")}
                        {renderNavLink("Assessments", "/assessments")}
                      </>
                    )}
                  </div>
                  <div className="flex items-center space-x-6">
                    {hasTeams && <TeamSelector />}
                    <ProfileDropdown user={user} onSignOut={handleSignOut} />
                  </div>
                </div>
              </>
            ) : (
              <>
                <div />
                {renderNavLink("Log in", "/")}
              </>
            )}
          </div>
          <MobileNav
            isAuthenticated={!!user}
            onSignOut={handleSignOut}
            onSignIn={handleSignIn}
          />
        </div>
      </nav>

      <main>
        <AppRoutes />
      </main>
    </div>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <AuthProvider>
          {({ isLoading }) =>
            isLoading ? (
              <div className="flex items-center justify-center min-h-screen">
                <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <>
                <AppContent />
                <Toaster />
              </>
            )
          }
        </AuthProvider>
      </Router>
    </QueryClientProvider>
  );
}

export default App;
